import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createOfflineQueue, memoryStorage, type OfflineQueue, type QueueStorage } from './queue.ts'

/**
 * The offline write queue, injected the same way the API client is.
 *
 * Mobile passes a file-backed storage. For attendance the web passes nothing
 * and gets an in-memory queue, which is honest there — a browser tab that
 * closes has not promised to remember anything.
 *
 * Check-in is the exception, and has a provider of its own below. A kiosk runs
 * unattended for two hours and a reload must not lose the children already
 * through the door, so the web passes IndexedDB-backed storage there.
 */

export interface QueuedAttendance {
  groupId: string
  occurrenceDate: string
  body: unknown
}

const QueueContext = createContext<OfflineQueue<QueuedAttendance> | null>(null)

export function OfflineQueueProvider({
  storage,
  children,
}: {
  storage?: QueueStorage
  children: ReactNode
}) {
  const queue = useMemo(
    () => createOfflineQueue<QueuedAttendance>(storage ?? memoryStorage()),
    [storage],
  )
  return <QueueContext.Provider value={queue}>{children}</QueueContext.Provider>
}

/**
 * Null when no provider is mounted, and callers must handle that: a screen
 * with no queue simply posts directly, which is what the web does.
 */
export function useOfflineQueue(): OfflineQueue<QueuedAttendance> | null {
  return useContext(QueueContext)
}

/**
 * The check-in queue.
 *
 * Separate from the attendance one because the payloads differ and because the
 * durability promise differs: attendance may be in memory, a check-in may not.
 * One queue per app rather than per screen — two queues over one storage would
 * read-modify-write over each other on a reconnect, which is the duplicate the
 * whole design exists to prevent.
 */
export interface QueuedCheckin {
  clientId: string
  stationId: string
  personId: string
  locationId: string
  securityCode: string
  clientCheckedInAt: string
  method: 'station' | 'self' | 'mobile' | 'manual'
  /** The service it is for, when the church has set times. */
  checkinTimeId?: string
  /** An adult checked in to serve. */
  kind?: 'volunteer'
  /** Never sent. Carried so a label can be reprinted after a reload. */
  label: { personName: string; locationName: string; labelNotes: string | null }
}

const CheckinQueueContext = createContext<OfflineQueue<QueuedCheckin> | null>(null)

export function CheckinQueueProvider({
  storage,
  children,
}: {
  storage?: QueueStorage
  children: ReactNode
}) {
  const queue = useMemo(
    () => createOfflineQueue<QueuedCheckin>(storage ?? memoryStorage()),
    [storage],
  )
  return <CheckinQueueContext.Provider value={queue}>{children}</CheckinQueueContext.Provider>
}

export function useCheckinQueue(): OfflineQueue<QueuedCheckin> | null {
  return useContext(CheckinQueueContext)
}
